import { Link } from 'react-router';
import { SEO, BreadcrumbSchema, FAQSchema, ServiceSchema, SpeakableSchema } from '@/components/seo/seo';

function RatingBadge({ rating, size = 'md' }: { rating: 'red' | 'amber' | 'green'; size?: 'sm' | 'md' | 'lg' }) {
  const colors = {
    red: 'bg-rose-500 text-white',
    amber: 'bg-amber-500 text-white',
    green: 'bg-emerald-500 text-white',
  };
  const sizes = {
    sm: 'px-2 py-0.5 text-xs',
    md: 'px-3 py-1 text-sm',
    lg: 'px-4 py-2 text-base',
  };
  return (
    <span className={`inline-block font-bold rounded-full uppercase ${colors[rating]} ${sizes[size]}`}>
      {rating}
    </span>
  );
}

function QuoteCard({ quote, source }: { quote: string; source: string }) {
  return (
    <blockquote className="bg-surface-50 border-l-4 border-brand-500 p-6 rounded-r-xl">
      <p className="text-surface-800 italic">"{quote}"</p>
      <cite className="text-sm text-surface-600 mt-2 block not-italic">— {source}</cite>
    </blockquote>
  );
}

const faqs = [
  {
    question: 'Westmorland and Furness is RED overall — does that guarantee my claim will succeed?',
    answer: 'No guarantee, but it frames your argument. The Department for Transport rates the council RED overall with AMBER scorecards for condition, spend and best practice — driven partly by U-road condition scores and reactive repair volumes. Section 58 still turns on whether the specific defect that damaged your vehicle was reasonably inspected and repaired. The 34,228 pothole fills in 2024/25 and worsening U-road RED percentages strengthen a claim that the network-level system is struggling, but your photos, prior reports and road class matter most.',
  },
  {
    question: 'What if my pothole was on a residential or unclassified road?',
    answer: 'Unclassified roads make up 1,942km — 45% of Westmorland and Furness\'s 4,344km carriageway network. The council publishes only RED-condition percentages for U-roads (26.9% in 2024, up from 24.8% in 2023), not full red/amber/green breakdowns. It deploys annual AI-led surveys across the entire U-class network. If your incident was on a U-road, ask whether the council can produce inspection dates, AI defect logs and repair scheduling for your specific street — not just network-wide averages.',
  },
  {
    question: 'The pothole count jumped after a recording change in 2023/24 — can I still cite 34,228?',
    answer: 'Yes, with context. The council states that from mid-2023/24 each defect was recorded by actual repair count, not as a single pothole per defect. That inflates 2024/25 figures relative to earlier years. But the pre-change trend is still stark: 6,487 fills in 2022/23 rose to 16,107 in 2023/24 (+148%) before the methodology changed. The council itself plans 30,000–40,000 pothole repairs in 2025/26 — evidence that reactive workload remains structural, not a recording artefact.',
  },
  {
    question: 'What if the council produces AI inspection data for my U-road?',
    answer: 'Then they must connect that data to your defect. They need to show the AI survey met their standards, when your road was last captured, whether the defect was absent or below intervention thresholds at inspection, and — if it was reportable — why it was not repaired within stated timeframes. U-road RED condition worsened year-on-year despite AI deployment. If AI captured a defect and the council did not repair it before your incident, that undermines a Section 58 defence.',
  },
  {
    question: 'Does the council being formed in April 2023 really matter?',
    answer: 'It can. Westmorland and Furness Council was created through local government reorganisation in April 2023. The transparency report states condition data exists only from that point, and spending figures before 2023/24 are not applicable. For Section 58, the council must show a reasonable inspection and maintenance system was maintained consistently — including continuity of records and standards from predecessor authorities (South Lakeland, Barrow, Eden and others). Gaps in transferred inspection history are arguable weak points.',
  },
  {
    question: 'The council claims 87.99% preventative spending — does that block my claim?',
    answer: 'Not automatically. The DfT Spend scorecard is AMBER, with projected 2025/26 capital spend of £33.1m against a DfT allocation of £4.5m. The council classifies nearly 88% of maintenance as preventative — yet it filled 34,228 potholes in 2024/25 and budgets £8.4m for 30,000–40,000 more repairs in 2025/26. Aggregate spend and categorisation do not prove the individual defect on your road was known and repaired within inspection intervals.',
  },
  {
    question: 'How do I report a pothole to Westmorland and Furness Council?',
    answer: 'Report street or road damage via the council\'s online form at westmorlandandfurness.gov.uk, or call the Highways team on 0300 373 3306 (Monday to Friday, 8.30am to 5pm). Prior reports of the same defect strengthen a claim by demonstrating the council had notice before your incident. Fixtyer searches for existing reports and attaches them to your claim pack.',
  },
];

export function WestmorlandAndFurnessCouncilPage() {
  return (
    <div className="min-h-screen">
      <SEO
        title="Westmorland and Furness Pothole Claims | 34,228 Repairs on a Red-Rated Network | Fixtyer"
        description="Westmorland and Furness earns a RED DfT rating, filled 34,228 potholes in 2024/25, and U-road RED condition rose to 26.9% despite AI inspection. What that means for your Section 58 claim."
        canonical="/councils/westmorland-and-furness"
      />
      <BreadcrumbSchema
        items={[
          { name: 'Home', url: '/' },
          { name: 'Councils', url: '/councils' },
          { name: 'Westmorland and Furness', url: '/councils/westmorland-and-furness' },
        ]}
      />
      <FAQSchema faqs={faqs} />
      <ServiceSchema
        councilName="Westmorland and Furness Council"
        councilSlug="westmorland-and-furness"
        rating="red"
      />
      <SpeakableSchema
        url="/councils/westmorland-and-furness"
        headline="Westmorland and Furness Road Maintenance Report"
        cssSelectors={['[data-speakable="summary"]', 'h1', 'h2']}
      />

      {/* Hero */}
      <section className="relative bg-brand-950 text-white overflow-hidden">
        <div
          className="absolute top-0 left-0 right-0 h-8 z-10"
          style={{
            background: `repeating-linear-gradient(
              -45deg,
              #f97316,
              #f97316 40px,
              #1e40af 40px,
              #1e40af 80px
            )`,
          }}
        />

        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 pt-20 pb-16">
          <nav className="text-sm text-brand-400 mb-8 flex items-center gap-2">
            <Link to="/" className="hover:text-white transition-colors">Home</Link>
            <span>/</span>
            <Link to="/councils" className="hover:text-white transition-colors">Councils</Link>
            <span>/</span>
            <span className="text-white">Westmorland and Furness</span>
          </nav>

          <div className="flex flex-wrap items-center gap-3 mb-4">
            <RatingBadge rating="red" size="lg" />
            <span className="text-brand-300">Overall</span>
            <span className="text-brand-500">|</span>
            <span className="flex items-center gap-2">
              <RatingBadge rating="amber" size="sm" /> <span className="text-brand-300 text-sm">Condition</span>
            </span>
            <span className="flex items-center gap-2">
              <RatingBadge rating="amber" size="sm" /> <span className="text-brand-300 text-sm">Spend</span>
            </span>
            <span className="flex items-center gap-2">
              <RatingBadge rating="amber" size="sm" /> <span className="text-brand-300 text-sm">Best Practice</span>
            </span>
          </div>

          <div className="bg-amber-500/10 border border-amber-500/30 rounded-xl px-4 py-3 mb-6 max-w-3xl">
            <p className="text-amber-200 text-sm">
              <strong className="text-amber-400">Source caveat:</strong> Westmorland and Furness Council was formed in
              April 2023. Its transparency report provides condition and spending data only from local government
              reorganisation — not full predecessor-authority history. Pothole counts changed recording methodology in
              mid-2023/24.
            </p>
          </div>

          <h1 className="font-display text-4xl sm:text-5xl font-bold leading-tight mb-6">
            34,228 Pothole Repairs in 2024/25 on a Red-Rated Rural Network
          </h1>

          <p className="text-xl text-brand-200 max-w-2xl mb-8 leading-relaxed" data-speakable="summary">
            Westmorland and Furness Council — a unitary authority formed in April 2023 — maintains{' '}
            <strong className="text-white">4,344km of roads</strong> worth an estimated{' '}
            <strong className="text-white">£4 billion</strong>. The DfT rates it{' '}
            <strong className="text-rose-400">RED</strong> overall with{' '}
            <strong className="text-amber-400">AMBER</strong> condition, spend and best-practice scorecards. Its own
            report records <strong className="text-rose-400">34,228 pothole fills in 2024/25</strong> — roughly{' '}
            <strong className="text-rose-400">94 repairs per day</strong> — while U-road RED condition rose from 24.8%
            to 26.9% despite AI-led inspection. Section 58 still turns on your specific defect.
          </p>

          <div className="bg-rose-500/20 border border-rose-500/30 rounded-2xl p-6 max-w-md">
            <div className="text-6xl font-bold text-rose-400">34,228</div>
            <div className="text-white font-medium mt-2">Potholes filled in 2024/25</div>
            <div className="text-brand-300 text-sm mt-1">
              Up from 3,797 in 2021/22 — an 800% rise even after accounting for mid-2023/24 recording changes. The
              council plans 30,000–40,000 more repairs in 2025/26 across 2,600 miles of Lake District and Furness
              highways.
            </div>
          </div>
        </div>

        <div
          className="absolute bottom-0 left-0 right-0 h-8 z-10"
          style={{
            background: `repeating-linear-gradient(
              -45deg,
              #1e40af,
              #1e40af 40px,
              #f97316 40px,
              #f97316 80px
            )`,
          }}
        />
      </section>

      {/* Network scale */}
      <section className="py-16 bg-white">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="font-display text-3xl font-bold text-brand-950 mb-4">
            4,344km Across the Lake District and Furness Peninsula
          </h2>
          <p className="text-surface-600 mb-8">
            Network scale from the council's June 2025 transparency report — where rural pothole claims actually happen
          </p>

          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4 mb-8">
            <div className="bg-surface-50 border border-surface-200 rounded-2xl p-6">
              <div className="text-3xl font-bold text-brand-900">4,344km</div>
              <div className="text-sm text-surface-700 font-medium mt-1">Total carriageway</div>
              <div className="text-xs text-surface-500 mt-1">~2,600 miles</div>
            </div>
            <div className="bg-rose-50 border border-rose-200 rounded-2xl p-6">
              <div className="text-3xl font-bold text-rose-600">1,942km</div>
              <div className="text-sm text-rose-800 font-medium mt-1">Unclassified (U) roads</div>
              <div className="text-xs text-rose-700 mt-1">45% of the network</div>
            </div>
            <div className="bg-surface-50 border border-surface-200 rounded-2xl p-6">
              <div className="text-3xl font-bold text-brand-900">1,606km</div>
              <div className="text-sm text-surface-700 font-medium mt-1">B and C roads</div>
              <div className="text-xs text-surface-500 mt-1">37% of network</div>
            </div>
            <div className="bg-surface-50 border border-surface-200 rounded-2xl p-6">
              <div className="text-3xl font-bold text-brand-900">507km</div>
              <div className="text-sm text-surface-700 font-medium mt-1">A roads</div>
              <div className="text-xs text-surface-500 mt-1">12% — includes tourist and trunk corridors</div>
            </div>
          </div>

          <div className="bg-white rounded-2xl border border-surface-200 overflow-hidden mb-6">
            <table className="w-full text-sm">
              <caption className="sr-only">Westmorland and Furness highway asset breakdown</caption>
              <thead>
                <tr className="bg-surface-100 border-b border-surface-200">
                  <th className="px-6 py-3 text-left font-semibold text-brand-900">Asset</th>
                  <th className="px-6 py-3 text-center font-semibold text-brand-900">Scale</th>
                </tr>
              </thead>
              <tbody>
                <tr className="border-b border-surface-100">
                  <td className="px-6 py-4">Unsurfaced roads</td>
                  <td className="px-6 py-4 text-center font-medium">289km (7%)</td>
                </tr>
                <tr className="border-b border-surface-100">
                  <td className="px-6 py-4">Footways</td>
                  <td className="px-6 py-4 text-center font-medium">1,431km</td>
                </tr>
                <tr className="border-b border-surface-100">
                  <td className="px-6 py-4">Public rights of way</td>
                  <td className="px-6 py-4 text-center font-medium">2,108km</td>
                </tr>
                <tr>
                  <td className="px-6 py-4">Cycleways</td>
                  <td className="px-6 py-4 text-center font-medium">889km</td>
                </tr>
              </tbody>
            </table>
          </div>

          <QuoteCard
            quote="Westmorland and Furness Council has over 2,600 miles of carriageway and an estimated value of £4 billion. The highway network is a significant asset that connects people and places across Westmorland and Furness contributing to the wellbeing of residents, thriving communities, and enabling people to access work, learning and business opportunities to fulfil their ambitions."
            source="Westmorland and Furness Council Local Highways Maintenance Transparency Report (June 2025)"
          />
        </div>
      </section>

      {/* Condition data */}
      <section className="py-16 bg-surface-50">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="font-display text-3xl font-bold text-brand-950 mb-4">
            What AMBER Condition Actually Shows
          </h2>
          <p className="text-surface-600 mb-8">
            Condition data from April 2023 only — AMBER scorecard driven by U-road deterioration
          </p>

          <div className="bg-amber-50 border border-amber-200 rounded-xl p-6 mb-8">
            <p className="text-amber-800 text-sm">
              <strong>LGR caveat:</strong> Due to local government reorganisation in April 2023, condition data exists
              only from when Westmorland and Furness Council was formed. Predecessor authority records are not included
              in published figures. For U-roads, the council publishes <strong>RED percentages only</strong> — not full
              red/amber/green breakdowns — despite deploying annual AI-led surveys across the entire U-class network.
            </p>
          </div>

          {/* A roads */}
          <div className="bg-white border border-surface-200 rounded-2xl p-6 mb-6">
            <h3 className="font-semibold text-brand-900 mb-4">A roads (507km) — surveyed annually, stable</h3>
            <div className="bg-surface-50 rounded-xl overflow-hidden border border-surface-200 mb-4">
              <table className="w-full text-sm">
                <caption className="sr-only">Westmorland and Furness A-road condition 2023 to 2024</caption>
                <thead>
                  <tr className="bg-surface-100 border-b border-surface-200">
                    <th className="px-4 py-2 text-left font-semibold text-brand-900">Year</th>
                    <th className="px-4 py-2 text-center font-semibold text-rose-700">Red</th>
                    <th className="px-4 py-2 text-center font-semibold text-amber-700">Amber</th>
                    <th className="px-4 py-2 text-center font-semibold text-emerald-700">Green</th>
                  </tr>
                </thead>
                <tbody>
                  <tr className="border-b border-surface-100">
                    <td className="px-4 py-2 font-mono">2023</td>
                    <td className="px-4 py-2 text-center">3.8%</td>
                    <td className="px-4 py-2 text-center">25.7%</td>
                    <td className="px-4 py-2 text-center">70.5%</td>
                  </tr>
                  <tr className="bg-emerald-50">
                    <td className="px-4 py-2 font-mono font-bold">2024</td>
                    <td className="px-4 py-2 text-center font-bold text-emerald-600">3.8%</td>
                    <td className="px-4 py-2 text-center font-bold">25.7%</td>
                    <td className="px-4 py-2 text-center font-bold text-emerald-600">70.5%</td>
                  </tr>
                </tbody>
              </table>
            </div>
            <p className="text-surface-600 text-sm">
              A-road condition is stable year-on-year at 70.5% green — but A-roads are just 12% of the carriageway
              network. Some major routes (including parts of the A66) are maintained by National Highways, not the
              council.
            </p>
          </div>

          {/* B&C roads */}
          <div className="bg-white border border-emerald-200 rounded-2xl p-6 mb-6">
            <h3 className="font-semibold text-brand-900 mb-4">B and C roads (1,606km) — slight improvement</h3>
            <div className="bg-surface-50 rounded-xl overflow-hidden border border-surface-200 mb-4">
              <table className="w-full text-sm">
                <caption className="sr-only">Westmorland and Furness B and C road condition 2023 to 2024</caption>
                <thead>
                  <tr className="bg-surface-100 border-b border-surface-200">
                    <th className="px-4 py-2 text-left font-semibold text-brand-900">Year</th>
                    <th className="px-4 py-2 text-center font-semibold text-rose-700">Red</th>
                    <th className="px-4 py-2 text-center font-semibold text-amber-700">Amber</th>
                    <th className="px-4 py-2 text-center font-semibold text-emerald-700">Green</th>
                  </tr>
                </thead>
                <tbody>
                  <tr className="border-b border-surface-100">
                    <td className="px-4 py-2 font-mono">2023</td>
                    <td className="px-4 py-2 text-center">10.5%</td>
                    <td className="px-4 py-2 text-center">36.6%</td>
                    <td className="px-4 py-2 text-center">52.9%</td>
                  </tr>
                  <tr className="bg-emerald-50">
                    <td className="px-4 py-2 font-mono font-bold">2024</td>
                    <td className="px-4 py-2 text-center font-bold text-emerald-600">9.45%</td>
                    <td className="px-4 py-2 text-center font-bold">37.2%</td>
                    <td className="px-4 py-2 text-center font-bold text-emerald-600">53.3%</td>
                  </tr>
                </tbody>
              </table>
            </div>
            <p className="text-surface-600 text-sm">
              B-roads are surveyed annually (100% coverage); C-roads at 50% coverage annually. RED condition fell
              marginally from 10.5% to 9.45% — a modest gain on 37% of the network.
            </p>
          </div>

          {/* U roads */}
          <div className="bg-rose-50 border border-rose-200 rounded-2xl p-6 mb-8">
            <h3 className="font-semibold text-rose-900 mb-4">Unclassified roads (1,942km) — deteriorating despite AI</h3>
            <div className="bg-white rounded-xl overflow-hidden border border-rose-200 mb-4">
              <table className="w-full text-sm">
                <caption className="sr-only">Westmorland and Furness U-road RED condition 2023 to 2024</caption>
                <thead>
                  <tr className="bg-rose-100 border-b border-rose-200">
                    <th className="px-4 py-2 text-left font-semibold text-rose-900">Year</th>
                    <th className="px-4 py-2 text-center font-semibold text-rose-700">Red (published)</th>
                    <th className="px-4 py-2 text-center font-semibold text-surface-500">Amber / Green</th>
                  </tr>
                </thead>
                <tbody>
                  <tr className="border-b border-rose-100">
                    <td className="px-4 py-2 font-mono">2023</td>
                    <td className="px-4 py-2 text-center">24.8%</td>
                    <td className="px-4 py-2 text-center text-surface-400 italic">Not published</td>
                  </tr>
                  <tr className="bg-rose-50">
                    <td className="px-4 py-2 font-mono font-bold">2024</td>
                    <td className="px-4 py-2 text-center font-bold text-rose-600">26.9%</td>
                    <td className="px-4 py-2 text-center text-surface-400 italic">Not published</td>
                  </tr>
                </tbody>
              </table>
            </div>
            <p className="text-rose-800 font-medium">
              U-road RED condition rose 2.1 percentage points — roughly 41km of additional residential and estate roads
              slipping into the worst category. At 26.9% RED, approximately 523km of U-roads require maintenance
              treatment. These are the roads most residents use daily.
            </p>
          </div>

          <QuoteCard
            quote="Each year, we conduct a comprehensive inspection of our entire U-class road network to ensure safety and maintenance standards are upheld. As part of this process, we deploy advanced artificial intelligence (AI) technology that systematically captures, analyzes, and reports the condition of these roads."
            source="Westmorland and Furness Council Local Highways Maintenance Transparency Report (June 2025)"
          />
        </div>
      </section>

      {/* Pothole repairs */}
      <section className="py-16 bg-white">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="font-display text-3xl font-bold text-brand-950 mb-4">
            From 3,797 to 34,228 in Four Years
          </h2>
          <p className="text-surface-600 mb-8">
            Estimated potholes filled — with a mid-2023/24 recording methodology change
          </p>

          <div className="bg-amber-50 border border-amber-200 rounded-xl p-6 mb-8">
            <p className="text-amber-800 text-sm">
              <strong>Recording caveat:</strong> The method for counting potholes filled changed in mid-2023/24. Before
              that date, one defect was recorded as one pothole; afterwards, the actual number of repairs on each defect
              was recorded. That inflates 2024/25 relative to earlier years — but 2023/24 already showed 16,107 fills
              (+148% on 2022/23) before the change took effect.
            </p>
          </div>

          <div className="bg-white rounded-2xl border border-surface-200 overflow-hidden mb-8">
            <table className="w-full">
              <caption className="sr-only">Westmorland and Furness estimated potholes filled 2021 to 2025</caption>
              <thead>
                <tr className="bg-surface-100 border-b border-surface-200">
                  <th className="px-6 py-3 text-left text-sm font-semibold text-brand-900">Year</th>
                  <th className="px-6 py-3 text-center text-sm font-semibold text-brand-900">Potholes filled</th>
                  <th className="px-6 py-3 text-center text-sm font-semibold text-brand-900">Change vs prior year</th>
                </tr>
              </thead>
              <tbody className="text-sm">
                <tr className="border-b border-surface-100">
                  <td className="px-6 py-4 font-mono text-brand-900">2021/22</td>
                  <td className="px-6 py-4 text-center">3,797</td>
                  <td className="px-6 py-4 text-center text-surface-500">Baseline</td>
                </tr>
                <tr className="border-b border-surface-100">
                  <td className="px-6 py-4 font-mono text-brand-900">2022/23</td>
                  <td className="px-6 py-4 text-center">6,487</td>
                  <td className="px-6 py-4 text-center text-amber-600">+71%</td>
                </tr>
                <tr className="border-b border-surface-100">
                  <td className="px-6 py-4 font-mono text-brand-900">2023/24</td>
                  <td className="px-6 py-4 text-center">16,107</td>
                  <td className="px-6 py-4 text-center text-rose-600">+148%</td>
                </tr>
                <tr className="bg-rose-50">
                  <td className="px-6 py-4 font-mono font-bold text-brand-900">2024/25</td>
                  <td className="px-6 py-4 text-center font-bold text-rose-600">34,228</td>
                  <td className="px-6 py-4 text-center font-bold text-rose-600">+112%</td>
                </tr>
                <tr className="bg-brand-50">
                  <td className="px-6 py-4 font-bold text-brand-900">2025/26 (planned)</td>
                  <td className="px-6 py-4 text-center font-bold text-amber-600">30,000–40,000</td>
                  <td className="px-6 py-4 text-center text-surface-500">Council estimate</td>
                </tr>
                <tr className="bg-surface-100">
                  <td className="px-6 py-4 font-bold text-brand-900">Four-year total (2021/22–2024/25)</td>
                  <td className="px-6 py-4 text-center font-bold text-rose-600">60,619</td>
                  <td className="px-6 py-4 text-center font-bold text-rose-600">800% rise since 2021/22</td>
                </tr>
              </tbody>
            </table>
          </div>

          <div className="grid md:grid-cols-3 gap-6 mb-8">
            <div className="bg-rose-50 border border-rose-200 rounded-xl p-6 text-center">
              <div className="text-4xl font-bold text-rose-600">94</div>
              <div className="text-rose-800 font-medium">Repairs per day</div>
              <div className="text-rose-600 text-sm mt-1">Based on 34,228 in 2024/25</div>
            </div>
            <div className="bg-amber-50 border border-amber-200 rounded-xl p-6 text-center">
              <div className="text-4xl font-bold text-amber-600">13,000</div>
              <div className="text-amber-800 font-medium">Found by inspections</div>
              <div className="text-amber-600 text-sm mt-1">Council potholes page, 2024/25</div>
            </div>
            <div className="bg-surface-50 border border-surface-200 rounded-xl p-6 text-center">
              <div className="text-4xl font-bold text-brand-900">2,555</div>
              <div className="text-surface-700 font-medium">Reported by public</div>
              <div className="text-surface-500 text-sm mt-1">Of ~15,600 system-recorded defects</div>
            </div>
          </div>

          <div className="bg-brand-950 text-white rounded-2xl p-8">
            <h3 className="font-display text-xl font-bold mb-4">Why this matters for your claim</h3>
            <p className="text-brand-200 mb-4">
              Filling 34,228 potholes in one year does not demonstrate a "reasonable system" under Section 58. It
              demonstrates reactive workload at scale — defects forming faster than preventative resurfacing prevents
              them, despite claimed 87.99% preventative spending.
            </p>
            <ul className="text-brand-300 space-y-2 text-sm">
              <li>• Can the council produce the inspection date and defect log for your specific road?</li>
              <li>• Was your pothole among the 13,000 found by inspection, or only discovered after damage?</li>
              <li>• If AI surveys captured your street, what did they show — and when was repair scheduled?</li>
            </ul>
          </div>
        </div>
      </section>

      {/* Spending */}
      <section className="py-16 bg-brand-950 text-white">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="font-display text-3xl font-bold mb-4">
            Following the Money
          </h2>
          <p className="text-brand-200 mb-8 max-w-2xl">
            AMBER spend scorecard — high investment, but reactive repairs still dominate the story
          </p>

          <div className="bg-brand-900/50 rounded-2xl border border-brand-800 overflow-hidden mb-8">
            <table className="w-full text-sm">
              <caption className="sr-only">Westmorland and Furness highway maintenance spending 2023 to 2026</caption>
              <thead>
                <tr className="bg-brand-800 border-b border-brand-700">
                  <th className="px-4 py-3 text-left font-semibold text-brand-300">Year</th>
                  <th className="px-4 py-3 text-center font-semibold text-brand-300">DfT capital (£000s)</th>
                  <th className="px-4 py-3 text-center font-semibold text-brand-300">Capital spend (£000s)</th>
                  <th className="px-4 py-3 text-center font-semibold text-brand-300">Preventative</th>
                  <th className="px-4 py-3 text-center font-semibold text-brand-300">Reactive</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-brand-800">
                <tr className="bg-amber-900/30">
                  <td className="px-4 py-3 font-medium">2025/26 (proj.)</td>
                  <td className="px-4 py-3 text-center">4,525</td>
                  <td className="px-4 py-3 text-center font-bold text-amber-400">33,142</td>
                  <td className="px-4 py-3 text-center text-emerald-400">87.99%</td>
                  <td className="px-4 py-3 text-center">12.01%</td>
                </tr>
                <tr>
                  <td className="px-4 py-3">2024/25</td>
                  <td className="px-4 py-3 text-center">4,143</td>
                  <td className="px-4 py-3 text-center">29,341</td>
                  <td className="px-4 py-3 text-center">87.63%</td>
                  <td className="px-4 py-3 text-center">12.37%</td>
                </tr>
                <tr>
                  <td className="px-4 py-3">2023/24</td>
                  <td className="px-4 py-3 text-center">4,595</td>
                  <td className="px-4 py-3 text-center">22,648</td>
                  <td className="px-4 py-3 text-center">83.13%</td>
                  <td className="px-4 py-3 text-center">16.87%</td>
                </tr>
                <tr>
                  <td className="px-4 py-3 text-surface-400">2021/22 – 2022/23</td>
                  <td className="px-4 py-3 text-center text-surface-500 italic" colSpan={4}>
                    Not applicable — council formed April 2023
                  </td>
                </tr>
              </tbody>
            </table>
          </div>

          <div className="grid md:grid-cols-2 gap-6">
            <div className="bg-amber-500/20 rounded-xl p-6 border border-amber-500/30">
              <h3 className="font-semibold text-white mb-3">Why spend is AMBER, not GREEN</h3>
              <p className="text-brand-200 text-sm">
                Projected 2025/26 capital spend of £33.1m is more than seven times the DfT allocation of £4.5m — a
                credible investment story. But the DfT Spend scorecard also weighs how effectively money translates into
                network outcomes. U-road condition is worsening and pothole volumes are at crisis levels despite rising
                preventative spend shares.
              </p>
            </div>
            <div className="bg-rose-500/20 rounded-xl p-6 border border-rose-500/30">
              <h3 className="font-semibold text-white mb-3">The preventative spending paradox</h3>
              <p className="text-brand-200 text-sm">
                The council classifies 87.99% of maintenance as preventative — yet budgets £8.4m to fill 30,000–40,000
                potholes in 2025/26. Either preventative treatments are not preventing defects on U-roads, or the
                categorisation does not reflect on-the-ground reactive reality. Both interpretations help a
                evidence-led claim.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Planned work */}
      <section className="py-16 bg-white">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="font-display text-3xl font-bold text-brand-950 mb-4">
            Planned Work 2025/26
          </h2>
          <p className="text-surface-600 mb-8">
            £37.7 million highways investment — what the council says it will deliver
          </p>

          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4 mb-8">
            <div className="bg-rose-50 border border-rose-200 rounded-2xl p-6">
              <div className="text-3xl font-bold text-rose-600">30–40k</div>
              <div className="text-sm text-rose-800 font-medium mt-1">Pothole repairs planned</div>
              <div className="text-xs text-rose-700 mt-1">£8.4m pothole programme</div>
            </div>
            <div className="bg-surface-50 border border-surface-200 rounded-2xl p-6">
              <div className="text-3xl font-bold text-brand-900">59</div>
              <div className="text-sm text-surface-700 font-medium mt-1">Road resurfacing schemes</div>
              <div className="text-xs text-surface-500 mt-1">£7.5m programme</div>
            </div>
            <div className="bg-surface-50 border border-surface-200 rounded-2xl p-6">
              <div className="text-3xl font-bold text-brand-900">66 miles</div>
              <div className="text-sm text-surface-700 font-medium mt-1">Surface dressing</div>
              <div className="text-xs text-surface-500 mt-1">£3.3m — 53 locations</div>
            </div>
            <div className="bg-surface-50 border border-surface-200 rounded-2xl p-6">
              <div className="text-3xl font-bold text-brand-900">~100</div>
              <div className="text-sm text-surface-700 font-medium mt-1">Bridges and structures</div>
              <div className="text-xs text-surface-500 mt-1">£3.4m investment</div>
            </div>
          </div>

          <div className="mt-6 bg-surface-50 rounded-xl p-6 border border-surface-200">
            <h3 className="font-semibold text-brand-900 mb-3">Coverage maths</h3>
            <p className="text-surface-600 text-sm">
              Resurfacing 59 roads and surface-dressing 66 miles treats a fraction of 4,344km (2,600 miles) of
              carriageway in a single year. The remaining network relies on AI-led U-road surveys, reactive pothole
              filling and routine safety inspections — on roads where RED condition already sits at 26.9% and the
              council expects up to 40,000 more pothole repairs.
            </p>
          </div>
        </div>
      </section>

      {/* New council / LGR */}
      <section className="py-16 bg-surface-50">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="font-display text-3xl font-bold text-brand-950 mb-4">
            A New Council With Inherited Networks
          </h2>
          <p className="text-surface-600 mb-8">
            Formed April 2023 — limited published history, predecessor inspection records in question
          </p>

          <div className="grid md:grid-cols-2 gap-8 mb-8">
            <div className="bg-white rounded-2xl p-6 border border-surface-200">
              <h3 className="font-semibold text-brand-900 mb-4">What LGR created</h3>
              <ul className="text-surface-600 text-sm space-y-2">
                <li>• Unitary authority from South Lakeland, Barrow-in-Furness, Eden and other predecessor councils</li>
                <li>• One of England's largest rural highway networks — Lake District, coastal Furness, mountain passes</li>
                <li>• Condition and spending transparency data from April 2023 only</li>
                <li>• AI-led U-road surveys deployed from the new authority's first published year</li>
              </ul>
            </div>
            <div className="bg-blue-50 border border-blue-200 rounded-2xl p-6">
              <h3 className="font-semibold text-blue-900 mb-4">Section 58 continuity questions</h3>
              <p className="text-blue-800 text-sm mb-3">
                Section 58 requires proof of a <strong>reasonable system maintained consistently</strong> — not just
                since April 2023.
              </p>
              <ul className="text-blue-700 text-sm space-y-1">
                <li>• What inspection standards applied before LGR on your road?</li>
                <li>• Were predecessor inspection records transferred accurately?</li>
                <li>• Can the council demonstrate continuity from South Lakeland, Barrow or Eden systems?</li>
              </ul>
            </div>
          </div>

          <div className="space-y-6">
            <QuoteCard
              quote="Due to local government reorganization (LGR) with Westmorland and Furness Council coming into being in April 2023 the condition data has only been provided since that LGR."
              source="Westmorland and Furness Council Local Highways Maintenance Transparency Report (June 2025)"
            />
            <QuoteCard
              quote="The Highways network in Westmorland and Furness is an ageing asset and it has been affected by recent extreme weather events, which are becoming more regular. Maintaining this asset to the standards expected within the resources available will always remain a challenge."
              source="Westmorland and Furness Council Local Highways Maintenance Transparency Report (June 2025)"
            />
            <QuoteCard
              quote="Available funds are limited, therefore it is important to optimise our resources to get maximum value from the highway asset."
              source="Westmorland and Furness Council Local Highways Maintenance Transparency Report (June 2025)"
            />
          </div>
        </div>
      </section>

      {/* Council admissions */}
      <section className="py-16 bg-white">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="font-display text-3xl font-bold text-brand-950 mb-4">
            What the Council Acknowledges
          </h2>
          <p className="text-surface-600 mb-8">
            Verbatim admissions from the June 2025 transparency report
          </p>

          <div className="space-y-6">
            <div>
              <h3 className="font-semibold text-brand-900 mb-3">On ageing assets and extreme weather</h3>
              <QuoteCard
                quote="The Highways network in Westmorland and Furness is an ageing asset and it has been affected by recent extreme weather events, which are becoming more regular."
                source="Westmorland and Furness Council Local Highways Maintenance Transparency Report (June 2025)"
              />
            </div>

            <div>
              <h3 className="font-semibold text-brand-900 mb-3">On limited resources</h3>
              <QuoteCard
                quote="Available funds are limited, therefore it is important to optimise our resources to get maximum value from the highway asset."
                source="Westmorland and Furness Council Local Highways Maintenance Transparency Report (June 2025)"
              />
            </div>

            <div>
              <h3 className="font-semibold text-brand-900 mb-3">On statutory duty vs planned works</h3>
              <QuoteCard
                quote="In addition to this the Council uses its own funds to carry out reactive and general maintenance works to the highway network including: gully cleaning, verge maintenance, defect repairs, road marking maintenance."
                source="Westmorland and Furness Council Local Highways Maintenance Transparency Report (June 2025)"
              />
            </div>
          </div>

          <div className="mt-8 bg-amber-50 border border-amber-200 rounded-xl p-6">
            <h3 className="font-semibold text-amber-900 mb-3">What this means for claimants</h3>
            <p className="text-amber-800 text-sm">
              Weather damage, ageing infrastructure and limited budgets are the council's operational challenges — not
              your liability. Section 58 does not excuse a statutory duty because resources are tight. If the council
              admits it cannot maintain the network to expected standards, that is evidence the system may not be
              "reasonable" for your road.
            </p>
          </div>
        </div>
      </section>

      {/* Inspections and Section 58 */}
      <section className="py-16 bg-surface-50">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="font-display text-3xl font-bold text-brand-950 mb-4">
            Inspections, AI Surveys and Section 58
          </h2>
          <p className="text-surface-600 mb-8">
            How the council says it knows network condition — and where gaps appear
          </p>

          <div className="grid md:grid-cols-2 gap-6 mb-8">
            <div className="bg-white rounded-xl p-6 border border-surface-200">
              <h3 className="font-semibold text-brand-900 mb-3">Survey frequency</h3>
              <ul className="text-surface-600 text-sm space-y-2">
                <li>• <strong>A roads:</strong> annual SCANNER surveys, 100% coverage one direction</li>
                <li>• <strong>B roads:</strong> annual surveys, 100% coverage one direction</li>
                <li>• <strong>C roads:</strong> annual surveys, 50% coverage one direction</li>
                <li>• <strong>U roads:</strong> annual comprehensive AI-led inspection of entire network</li>
                <li>• <strong>From 2026/27:</strong> PAS 2161 five-category standard replaces three-band RAG</li>
              </ul>
            </div>
            <div className="bg-white rounded-xl p-6 border border-surface-200">
              <h3 className="font-semibold text-brand-900 mb-3">The AI inspection question</h3>
              <p className="text-surface-600 text-sm mb-3">
                If AI systematically captures, analyses and reports U-road condition annually, ask why RED U-road
                percentages rose from 24.8% to 26.9% and why 34,228 potholes needed filling in 2024/25.
              </p>
              <p className="text-surface-600 text-sm">
                The council's potholes page states 13,000 defects were found through inspections and 2,555 reported by
                the public — yet over 33,000 were fixed in total, including backlog and defects found during nearby
                repairs.
              </p>
            </div>
          </div>

          <div className="bg-brand-950 text-white rounded-2xl p-8">
            <h3 className="font-display text-xl font-bold mb-4">Section 41 vs Section 58</h3>
            <p className="text-brand-200 mb-4">
              Under <strong className="text-white">Section 41 of the Highways Act 1980</strong>, Westmorland and
              Furness must maintain public highways. To defend a claim under{' '}
              <strong className="text-white">Section 58</strong>, it must show a reasonable system for inspecting and
              repairing the <strong className="text-white">specific defect</strong> — not just publish highways
              investment plans.
            </p>
            <ul className="text-brand-300 space-y-2 text-sm mb-6">
              <li>• Date and time of last inspection of your specific road</li>
              <li>• Inspector notes or AI data identifying any defects at survey</li>
              <li>• Evidence the defect was not present or not reportable at inspection</li>
              <li>• If reportable, evidence of scheduling repair within stated timeframes</li>
              <li>• Predecessor-authority inspection records if your incident predates April 2023</li>
            </ul>
            <p className="text-rose-400 font-medium">
              Without road-specific evidence, a RED-rated reactive maintenance pattern undermines the Section 58 defence.
            </p>
          </div>
        </div>
      </section>

      {/* Balanced claims section */}
      <section className="py-16 bg-white">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="font-display text-3xl font-bold text-brand-950 mb-4">
            Claiming Against a Red-Rated Rural Authority
          </h2>
          <p className="text-surface-600 mb-8">
            Honest assessment: high spend and AI surveys exist — but outcomes are deteriorating on U-roads
          </p>

          <div className="grid md:grid-cols-2 gap-8 mb-8">
            <div className="bg-surface-50 rounded-2xl p-6 border border-surface-200">
              <h3 className="font-semibold text-brand-900 mb-4">What works in the council's favour</h3>
              <ul className="text-surface-700 space-y-2 text-sm">
                <li className="flex items-start gap-2">
                  <span className="text-emerald-500">✓</span> AMBER (not RED) condition, spend and best-practice scorecards
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-emerald-500">✓</span> Capital spend ~7× DfT allocation (£33.1m vs £4.5m projected)
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-emerald-500">✓</span> 87.99% preventative spend claimed for 2025/26
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-emerald-500">✓</span> Annual AI-led surveys across entire U-road network
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-emerald-500">✓</span> A-road and B/C road condition stable or improving
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-emerald-500">✓</span> £37.7m highways investment planned for 2025/26
                </li>
              </ul>
              <p className="text-surface-500 text-sm mt-4 italic">
                Expect the council to cite AI inspection, asset management strategy and high capital budgets in its
                Section 58 defence.
              </p>
            </div>

            <div className="bg-rose-50 border border-rose-200 rounded-2xl p-6">
              <h3 className="font-semibold text-rose-900 mb-4">What works in yours</h3>
              <ul className="text-rose-800 space-y-2 text-sm">
                <li className="flex items-start gap-2">
                  <span className="text-rose-500">✗</span> RED overall DfT rating
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-rose-500">✗</span> 34,228 pothole fills in 2024/25 — 94 repairs per day
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-rose-500">✗</span> U-road RED condition worsening: 24.8% → 26.9%
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-rose-500">✗</span> 1,942km of U-roads — 45% of network — where most claims start
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-rose-500">✗</span> LGR limited history — condition data from April 2023 only
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-rose-500">✗</span> Council admits ageing assets, extreme weather and limited funds
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-rose-500">✗</span> 30,000–40,000 more pothole repairs planned for 2025/26
                </li>
              </ul>
            </div>
          </div>

          <div className="bg-brand-950 text-white rounded-2xl p-8">
            <h3 className="font-display text-xl font-bold mb-4">The winning strategy here is specificity</h3>
            <p className="text-brand-200 mb-4">
              Against a council citing AI surveys and £37.7m investment, your claim lives or dies on the{' '}
              <strong className="text-white">specific defect</strong>:
            </p>
            <ul className="text-brand-300 space-y-2 mb-6">
              <li>• Prior reports of the same pothole — proof of actual notice beyond AI survey snapshots</li>
              <li>• Photos showing defect size, depth and visible age (weathered edges, previous patching)</li>
              <li>• Road class — on a U-road, worsening RED percentages and AI survey obligations are your structural arguments</li>
              <li>• Whether the council can produce AI inspection data and repair logs for your street, not network averages</li>
            </ul>
            <p className="text-white font-medium">
              Mac builds exactly this case: prior-report search, photo assessment, and citations from Westmorland and
              Furness's own transparency data — without overstating council-wide failure where A-roads are stable.
            </p>
          </div>
        </div>
      </section>

      {/* Report a pothole CTA */}
      <section className="py-12 bg-surface-100 border-y border-surface-200">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="font-display text-2xl font-bold text-brand-950 mb-3">
            Report a Pothole to Westmorland and Furness Council
          </h2>
          <p className="text-surface-600 mb-6">
            Reporting a defect creates a record the council had notice. Do this before claiming — and tell us when you
            reported it so we can reference it in your pack.
          </p>
          <a
            href="https://www.westmorlandandfurness.gov.uk/parking-streets-and-transport/streets-roads-and-pavements/road-maintenance-closures-and-improvements/report-street-or-road-damage"
            target="_blank"
            rel="noopener noreferrer"
            className="inline-flex items-center justify-center px-6 py-3 bg-brand-600 text-white font-semibold rounded-xl hover:bg-brand-700 transition-colors"
          >
            Report street or road damage — westmorlandandfurness.gov.uk
            <svg className="ml-2 w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14" />
            </svg>
          </a>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-16 bg-brand-950 text-white">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="font-display text-3xl font-bold mb-4">
            Hit a Pothole in Westmorland and Furness?
          </h2>
          <p className="text-xl text-brand-200 mb-8">
            A red-rated rural network demands a precise claim. £35 for a professional claim pack.
          </p>

          <div className="grid sm:grid-cols-2 gap-6 mb-8">
            <div className="bg-brand-900/50 rounded-xl p-6 border border-brand-800">
              <h3 className="font-semibold text-brand-300 mb-3">DIY claim</h3>
              <ul className="text-sm text-brand-400 space-y-1 text-left">
                <li>• Submit photos and invoices</li>
                <li>• Use generic template letter</li>
                <li>• No 34,228 pothole repair context</li>
                <li>• No U-road deterioration evidence</li>
                <li>• No LGR inspection-gap argument</li>
              </ul>
            </div>
            <div className="bg-accent-500/20 rounded-xl p-6 border border-accent-500/30">
              <h3 className="font-semibold text-white mb-3">Professional claim pack</h3>
              <ul className="text-sm text-brand-200 space-y-1 text-left">
                <li>✅ 34,228 pothole fills (94/day) documented</li>
                <li>✅ U-roads 26.9% RED deterioration cited</li>
                <li>✅ AI inspection claims challenged</li>
                <li>✅ LGR continuity argument included</li>
                <li>✅ Section 58 rebuttal tailored to Westmorland and Furness</li>
              </ul>
            </div>
          </div>

          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Link
              to="/check?council=westmorland-and-furness"
              className="inline-flex items-center justify-center px-8 py-4 bg-accent-500 text-white font-semibold rounded-xl hover:bg-accent-600 transition-all hover:shadow-lg hover:shadow-accent-500/25"
            >
              Order My Westmorland and Furness Claim Pack — £35
              <svg className="ml-2 w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 8l4 4m0 0l-4 4m4-4H3" />
              </svg>
            </Link>
            <Link
              to="/road-maintenance-report"
              className="inline-flex items-center justify-center px-8 py-4 bg-white/10 text-white font-semibold rounded-xl hover:bg-white/20 transition-colors"
            >
              View all council ratings
            </Link>
          </div>

          <p className="text-brand-400 text-sm mt-6">
            No percentage fees. You keep 100% of any compensation.
          </p>
        </div>
      </section>

      {/* FAQs */}
      <section className="py-16 bg-surface-50">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="font-display text-2xl font-bold text-brand-950 mb-8">
            Frequently asked questions
          </h2>

          <div className="space-y-6">
            {faqs.map((faq) => (
              <div key={faq.question} className="bg-white rounded-xl p-6 border border-surface-200">
                <h3 className="font-semibold text-brand-900 mb-2">{faq.question}</h3>
                <p className="text-surface-600 text-sm">{faq.answer}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Attribution Footer */}
      <section className="py-8 bg-surface-100 border-t border-surface-200">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
          <p className="text-xs text-surface-500 text-center">
            Data sources:{' '}
            <a
              href="https://www.gov.uk/government/publications/local-road-maintenance-ratings/local-road-maintenance-ratings-2025-to-2026"
              target="_blank"
              rel="noopener noreferrer"
              className="text-brand-600 hover:underline"
            >
              Department for Transport — Local Road Maintenance Ratings 2025 to 2026
            </a>{' '}
            |{' '}
            <a
              href="https://www.westmorlandandfurness.gov.uk/parking-streets-and-transport/streets-roads-and-pavements/road-maintenance-closures-and-improvements/plans-projects-and-improvements"
              target="_blank"
              rel="noopener noreferrer"
              className="text-brand-600 hover:underline"
            >
              Westmorland and Furness Council Local Highways Maintenance Transparency Report (June 2025)
            </a>
            . Contains public sector information licensed under the{' '}
            <a
              href="https://www.nationalarchives.gov.uk/doc/open-government-licence/version/3/"
              target="_blank"
              rel="noopener noreferrer"
              className="text-brand-600 hover:underline"
            >
              Open Government Licence v3.0
            </a>
            .
          </p>
        </div>
      </section>
    </div>
  );
}
